import { Helmet } from "react-helmet-async";
import {
  resolveSeo,
  useCollectionSeoTemplate,
  SITE_ORIGIN,
  SITEWIDE_OG_IMAGE,
  SITEWIDE_DESCRIPTION,
  type SeoItem,
} from "@/lib/seoTemplates";
import { getStaticPageSeo } from "@/lib/staticPageSeo";

interface Props {
  /** Collection key in collection_seo_templates (e.g. "insights.blog", "page.home"). */
  collectionKey?: string;
  /** CMS item, if any. For static pages, omit. */
  item?: SeoItem;
  /** Route path starting with "/", used for canonical and og:url. */
  path: string;
  /** og:type — defaults to "article" if item provided, else "website". */
  ogType?: "article" | "website";
  /** Override title (rare — only if not template-driven). */
  titleOverride?: string;
  /** Override description (rare). */
  descriptionOverride?: string;
}

/**
 * Per-route SEO head. Reads the collection template, merges with the item,
 * and emits Helmet tags (title, description, canonical, og:*, twitter:*).
 *
 * For CMS items, og:image defaults to the item's hero/cover image.
 * For static pages, og:image defaults to the sitewide image from index.html.
 * The SEO panel image (collection_seo_templates.og_image_url) overrides both.
 */
export default function SeoHead({
  collectionKey,
  item,
  path,
  ogType,
  titleOverride,
  descriptionOverride,
}: Props) {
  const { data: template } = useCollectionSeoTemplate(collectionKey);
  const url = `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
  const seo = resolveSeo(template, item ?? { title: "", summary: "", subtitle: "", category: "", author_name: "", event_starts_at: null, itemImage: null });

  // For static pages with no item & no template override, fall back to the
  // page's own literal copy (shared with the CMS panel preview) rather than
  // an empty head.
  const pageDefaults = !item && collectionKey?.startsWith("page.")
    ? getStaticPageSeo(collectionKey)
    : null;

  const title = titleOverride || seo.title || pageDefaults?.title;
  // Description floor: always emit a description so Helmet OWNS the tag (with
  // data-rh) and replaces the shell's homepage tagline on the broken clean-URL
  // path. For articles with empty CMS copy, this surfaces the sitewide tagline
  // — a floor, not a content fix. Real per-article descriptions still need to
  // be filled in the CMS.
  const description =
    descriptionOverride || seo.description || pageDefaults?.description || SITEWIDE_DESCRIPTION;
  const ogTitle = titleOverride || seo.ogTitle || pageDefaults?.title;
  const ogDescription =
    descriptionOverride || seo.ogDescription || pageDefaults?.description || SITEWIDE_DESCRIPTION;
  const ogImage = seo.ogImage || SITEWIDE_OG_IMAGE;
  const resolvedOgType = ogType ?? (item ? "article" : "website");

  return (
    <Helmet>
      {title && <title>{title}</title>}
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {seo.noindex && <meta name="robots" content="noindex, nofollow" />}
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={resolvedOgType} />
      <meta property="og:site_name" content="deepc" />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      {ogTitle && <meta name="twitter:title" content={ogTitle} />}
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
